import React, { CSSProperties, useContext, useEffect } from 'react';
import { IOfferContext, Locales, OfferContext, getLabelCollection } from '@adac-mp/shared';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { BranchOrderFooter, Dropdown, LanguageIcon } from '../../index';
import { useViewport } from '../../hooks/useViewport';

export const withLocalization = <T extends Record<string, any>>(WrappedComponent: React.FC<T>) => {
	const WithLocalization = ({ ...props }: T) => {
		const navigate = useNavigate();
		const location = useLocation();
		const { updateLang, updateLabels } = useContext<IOfferContext>(OfferContext);
		const { lang } = useParams();
		const { data } = useQuery(getLabelCollection, { variables: { lang: lang || 'de' } });
		const { viewport } = useViewport();
		const isCheckoutPage = location.pathname.includes('checkout');

		useEffect(() => {
			if (data) {
				updateLabels(data.labelCollection.items);
			}
		}, [data]);

		useEffect(() => {
			const currentLang = lang?.includes('?') ? lang.split('?')[0] : lang;
			updateLang(currentLang as Locales);
		}, [lang]);

		const dropdownStyle = (): CSSProperties => {
			let styles: CSSProperties;
			if (viewport === 'mobile') {
				styles = { paddingLeft: 28, paddingRight: 0, borderColor: 'transparent' };
			} else {
				styles = { paddingLeft: 52, paddingRight: 24, borderColor: '#FFFFFF29' };
			}
			return { ...styles, margin: 0, alignItems: 'flex-end', borderWidth: '1px', borderStyle: 'solid' };
		};

		const renderLanguageSwitcher = () => (
			<Dropdown
				data-cy="language: Dropdown"
				size="xs"
				dropdownColor="black"
				arrowVariant="bold"
				arrowSize={`${viewport === 'desktop' ? 16 : 24}`}
				className="w-full md:w-fit md:gap-2"
				contentClassName="w-[calc(100vw_-_64px)] md:w-[147.66px]"
				placeholder="Deutsch"
				icon={<LanguageIcon />}
				iconStyle={{ left: viewport === 'mobile' ? 0 : 24 }}
				style={dropdownStyle()}
				value={lang || 'de'}
				setValue={(val) => {
					const query = location.search;
					if (lang) {
						const pathname = location.pathname.replace(/\/$/, '');
						const shortenedPathname = pathname.substring(0, pathname.lastIndexOf('/'));
						navigate(`${shortenedPathname}/${val}${query}`);
					} else {
						navigate(`${location.pathname}/${val}${query}`);
					}
				}}
				items={[
					{
						value: 'de',
						label: 'Deutsch',
					},
					{
						value: 'en',
						label: 'English',
					},
				]}
			/>
		);

		return (
			<>
				<WrappedComponent {...props} />
				<div
					className={`px-5 bg-[#191919] md:grid md:items-center ${
						isCheckoutPage ? 'md:grid-cols-2' : 'md:grid-cols-1'
					}`}>
					<div className="h-[56px] flex items-center w-full relative after:content-[''] after:h-px after:w-full after:bg-gray-300 after:absolute after:left-0 after:bottom-0 md:justify-self-end md:order-1 md:flex-row-reverse">
						{renderLanguageSwitcher()}
					</div>
					{isCheckoutPage && <BranchOrderFooter />}
				</div>
			</>
		);
	};
	return WithLocalization;
};

withLocalization.displayName = 'withLocation';
